import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MonitorPlay } from 'lucide-react';
import { useMember, formatApiError } from '../../access/MemberAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  in_development: { label: 'In development', note: 'Information only. This experience is not available yet.' },
  register_interest: { label: 'Register interest', note: 'Not yet bookable. Register interest and we will contact you when sessions are confirmed. No payment is taken.' },
  bookable: { label: 'Book experience', note: 'Confirmed sessions with verified venue, equipment and supervision.' },
};

const InterestForm = ({ exp }) => {
  const { member } = useMember();
  const [f, setF] = useState({ parent_name: member?.first_name || '', email: member?.email || '', child_age_band: '5-7', access_notes: '', consent: false });
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/api/academy/experiences/${exp.slug}/interest`, f, { withCredentials: true }); setDone(true); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (done) return <p className="np-honesty" role="status" data-testid={`ac-interest-done-${exp.slug}`}>Thank you. Your interest is registered. We will only contact you about this experience.</p>;
  return (
    <form className="ac-form" onSubmit={submit} data-testid={`ac-interest-form-${exp.slug}`}>
      <label>Your name<input required maxLength={120} value={f.parent_name} onChange={set('parent_name')} data-testid={`ac-interest-name-${exp.slug}`} /></label>
      <label>Email<input required type="email" value={f.email} onChange={set('email')} data-testid={`ac-interest-email-${exp.slug}`} /></label>
      <label>Child&rsquo;s age band<select value={f.child_age_band} onChange={set('child_age_band')}><option>5-7</option><option>8-11</option><option>12-16</option><option>16-18</option></select></label>
      <label className="ac-wide">Anything that would help us plan (optional)<textarea rows={2} maxLength={600} value={f.access_notes} onChange={set('access_notes')} /></label>
      <label className="ac-consent ac-wide"><input type="checkbox" checked={f.consent} onChange={set('consent')} data-testid={`ac-interest-consent-${exp.slug}`} /> I agree that N3URO WORLD® may contact me about this experience. I am the child&rsquo;s parent or carer.</label>
      <button type="submit" className="conv-btn-primary" disabled={!f.consent} data-testid={`ac-interest-submit-${exp.slug}`}>Register interest</button>
    </form>
  );
};

const BookForm = ({ exp }) => {
  const { member } = useMember();
  const [children, setChildren] = useState([]);
  const [f, setF] = useState({ child_id: '', date: exp.dates?.[0] || '', access_needs: '' });
  const [booking, setBooking] = useState(null);
  useEffect(() => { if (member) axios.get(`${API}/api/academy/children`, { withCredentials: true }).then((r) => { setChildren(r.data); setF((p) => ({ ...p, child_id: r.data[0]?.id || '' })); }).catch(() => {}); }, [member]);
  if (!member) return <p className="ac-gate">Sign in with your N3URO Access account to book.</p>;
  if (booking) return <p className="np-honesty" role="status" data-testid={`ac-booked-${exp.slug}`}>Place reserved for {booking.childName} on {booking.date}. We will email preparation and arrival guidance.</p>;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await axios.post(`${API}/api/academy/experiences/${exp.slug}/book`, f, { withCredentials: true }); setBooking(data); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <form className="ac-form" onSubmit={submit} data-testid={`ac-book-form-${exp.slug}`}>
      <label>Child<select required value={f.child_id} onChange={set('child_id')}>{children.map((c) => <option key={c.id} value={c.id}>{c.first_name}</option>)}</select></label>
      <label>Session<select required value={f.date} onChange={set('date')}>{exp.dates.map((d) => <option key={d}>{d}</option>)}</select></label>
      <label className="ac-wide">Access needs to share (optional)<textarea rows={2} maxLength={600} value={f.access_needs} onChange={set('access_needs')} /></label>
      <button type="submit" className="conv-btn-primary" disabled={!f.child_id} data-testid={`ac-book-submit-${exp.slug}`}>Reserve a place{exp.price ? ` · ${exp.price}` : ''}</button>
    </form>
  );
};

const Fact = ({ k, v }) => (v ? <li><strong>{k}</strong><span>{v}</span></li> : null);

const SimulationExperiences = ({ pathway }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { axios.get(`${API}/api/academy/experiences?pathway=${pathway}`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [pathway]);
  return (
    <section className="ac-sim" aria-labelledby="ac-sim-h" data-testid="ac-simulation">
      <h2 id="ac-sim-h" className="np-h2"><MonitorPlay size={20} aria-hidden="true" /> Physical Simulation Experiences</h2>
      <p className="subpage-lede">Online learning and in-person, instructor-supported simulation are connected parts of the Academy: learn, experience and progress. Simulations are educational experiences, not professional pilot or train-driver training. You don&rsquo;t need to finish the online programme to register interest.</p>
      {items === null && <p className="kaz-loading" role="status">Loading experiences…</p>}
      {items?.map((x) => (
        <article key={x.slug} className="ac-exp" data-testid={`ac-exp-${x.slug}`}>
          <span className={`ac-exp-status ac-exp-status--${x.status}`} data-testid={`ac-exp-status-${x.slug}`}>{STATUS[x.status].label}</span>
          <h3>{x.title}</h3>
          <ul className="conv-facts">
            <Fact k="Ages" v={x.age_suitability} /><Fact k="Format" v={x.format} /><Fact k="Supervision" v={x.supervision} />
            <Fact k="Location" v={x.location || 'To be confirmed'} /><Fact k="Session length" v={x.session_length} />
            {x.status === 'bookable' && <Fact k="Places left" v={String(x.places_left)} />}
          </ul>
          {x.objectives?.length > 0 && <ul className="ac-objectives">{x.objectives.map((o) => <li key={o}>{o}</li>)}</ul>}
          <p className="np-help" data-testid={`ac-exp-capacity-${x.slug}`}>Groups are kept deliberately small, with no more than 12&ndash;14 children per session and age group.</p>
          {x.accessibility_notes && <p className="np-help">Accessibility: {x.accessibility_notes}</p>}
          <p className="np-help">{STATUS[x.status].note}</p>
          {x.status === 'register_interest' && <InterestForm exp={x} />}
          {x.status === 'bookable' && <BookForm exp={x} />}
        </article>
      ))}
      <p className="np-help">Schools, groups and sponsors: <a href="/academy/institutions">enquire about cohorts or commissioning</a>.</p>
    </section>
  );
};

export default SimulationExperiences;
